import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { db, auth } from "../firebase/firebase";
import { 
  createUserWithEmailAndPassword, 
  updatePassword, 
  sendPasswordResetEmail 
} from "firebase/auth";
import { 
  collection, 
  onSnapshot, 
  deleteDoc, 
  doc,
  setDoc,
  updateDoc,
  Timestamp
} from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Plus, Store, Globe, Eye, EyeOff, AlertCircle, FileText, Smartphone, Edit, Trash2, RefreshCw, X, Phone, Key
} from "lucide-react";

export default function StoreManager() {
  // --- MASTER STORAGE MANAGEMENTS ---
  const [selectedStoreId, setSelectedStoreId] = useState(""); 
  const [storesList, setStoresList] = useState([]);

  // --- INTERFACE UTILITIES ---
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [systemError, setSystemError] = useState(null);
  
  // --- EDIT MODE STATES ---
  const [isEditing, setIsEditing] = useState(false);
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [linkedAuthUid, setLinkedAuthUid] = useState(null);

  // --- COMPREHENSIVE REGISTRATION INITIAL STATES ---
  const [storeId, setStoreId] = useState(""); 
  const [storeCode, setStoreCode] = useState(""); 
  const [storeName, setStoreName] = useState(""); 
  const [ownerName, setOwnerName] = useState(""); 
  const [username, setUsername] = useState(""); 
  const [email, setEmail] = useState(""); 
  const [password, setPassword] = useState(""); 
  const [phone, setPhone] = useState(""); 
  const [whatsapp, setWhatsapp] = useState(""); 
  const [landline, setLandline] = useState(""); 
  const [role, setRole] = useState("STORE_ADMIN");
  const [status, setStatus] = useState("ACTIVE");

  // PHYSICAL METRIC FIELDS
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("Bihar");
  const [gstin, setStoreGSTIN] = useState("");

  const indianStates = ["Bihar", "Jharkhand", "Uttar Pradesh", "West Bengal", "Delhi", "Maharashtra"];

  // --- PLATFORM MASTER INITIALIZATION ---
  useEffect(() => {
    const provisionSuperAdmin = async () => {
      const targetSuperAdminUid = "7bEA4hgzOhQzFrHzHSlrrHe0hnv2"; 
      try {
        const userDocRef = doc(db, "users", targetSuperAdminUid);
        await setDoc(userDocRef, {
          name: "Mohit Kishor",
          email: "in.mohitkishor@gmail.com",
          role: "SUPER_ADMIN",
          createdAt: Timestamp.fromDate(new Date("2026-06-18T10:07:39+05:30"))
        }, { merge: true });
        console.log("Core Framework: SUPER_ADMIN authorization parameters validated.");
      } catch (err) {
        console.error("Super Admin Sync Interruption: ", err);
      }
    };
    provisionSuperAdmin();
  }, []);

  // --- GLOBAL STORES DATABASE LISTENER ---
  useEffect(() => {
    const unsubStores = onSnapshot(collection(db, "stores"), (snapshot) => {
      const pulledStores = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStoresList(pulledStores);
      if (pulledStores.length > 0 && !selectedStoreId) {
        setSelectedStoreId(pulledStores[0].id);
      }
    });
    return () => unsubStores();
  }, [selectedStoreId]);

  // --- FLUSH FORM ---
  const resetForm = () => {
    setStoreId(""); setStoreCode(""); setStoreName(""); setOwnerName(""); setUsername("");
    setEmail(""); setPassword(""); setPhone(""); setWhatsapp(""); setLandline(""); 
    setAddress(""); setCity(""); setStoreGSTIN("");
    setRole("STORE_ADMIN"); setStatus("ACTIVE");
    setIsEditing(false);
    setEditingStoreId(null);
    setLinkedAuthUid(null);
    setSystemError(null);
  };

  // --- DYNAMIC HANDLER: CREATE / UPDATE STORE ---
  const handleSaveStoreProfile = async (e) => {
    e.preventDefault();
    const cleanStoreId = storeId.trim().toUpperCase().replace(/\s+/g, '_');
    
    if (!cleanStoreId || !storeName.trim() || !storeCode.trim()) {
      setSystemError("Validation Mismatch: Store ID, Code, and Name are required.");
      return;
    }

    if (!email.trim() || (!isEditing && !password.trim())) {
      setSystemError("Validation Mismatch: Email and Password are required.");
      return;
    }
    
    if (password && password.length < 6) {
      setSystemError("Security Warning: Passwords must hit minimum 6 character parameters.");
      return;
    }

    if (landline.trim() && landline.trim().length !== 11) {
      setSystemError("Validation Mismatch: Landline number must be exactly 11 digits.");
      return;
    }

    setIsProcessing(true);
    setSystemError(null);

    try {
      if (isEditing) {
        // --- UPDATE EXISTING STORE IN FIRESTORE ---
        const storeDocRef = doc(db, "stores", editingStoreId);
        
        // Build update payload
        const updatePayload = {
          storeCode: storeCode.trim().toUpperCase(),
          storeName: storeName.trim(),
          ownerName: ownerName.trim(),
          username: username.trim() || storeCode.trim().toUpperCase(),
          phone: phone.trim(),
          whatsapp: whatsapp.trim(),
          landline: landline.trim(),
          role: role,
          status: status,
          address: address.trim(),
          city: city.trim(),
          state: state,
          gstin: gstin.trim().toUpperCase(),
          updatedAt: new Date().toISOString()
        };

        // Only update password in firestore if admin typed a new one
        if (password.trim()) {
          updatePayload.password = password.trim();
        }

        await updateDoc(storeDocRef, updatePayload);

        // Also update the linked user profile if it exists
        if (linkedAuthUid) {
          const userProfileDocRef = doc(db, "users", linkedAuthUid);
          await updateDoc(userProfileDocRef, {
            username: username.trim() || storeCode.trim().toUpperCase(),
            role: role,
            status: status
          });
        }

        // --- ATTEMPT CLIENT-SIDE AUTH PASSWORD UPDATE ---
        if (password.trim() && auth.currentUser) {
          if (auth.currentUser.uid === linkedAuthUid) {
            // If the admin is editing THEIR OWN account, Firebase allows this client-side
            try {
              await updatePassword(auth.currentUser, password.trim());
              console.log("Firebase Auth password successfully synced.");
            } catch (authErr) {
              console.error("Auth password update failed:", authErr);
              setSystemError("Firestore updated, but Auth password failed to sync. You may need to re-login.");
            }
          } else {
            // Alerting the user about the Firebase Admin SDK limitation
            alert("Firestore updated! Note: You cannot directly alter OTHER users' Firebase Auth login passwords from a frontend app. If they cannot log in, use the 'Send Password Reset Email' button.");
          }
        } else {
          alert("Success: Store profile successfully updated.");
        }

      } else {
        // --- CREATE NEW STORE ---
        const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password.trim());
        const authUserUid = userCredential.user.uid;

        const userProfileDocRef = doc(db, "users", authUserUid);
        const userPayload = {
          uid: authUserUid,
          username: username.trim() || storeCode.trim().toUpperCase(),
          email: email.trim(),
          role: role,
          status: status, 
          storeId: cleanStoreId, 
          createdAt: Timestamp.now() 
        };
        await setDoc(userProfileDocRef, userPayload);

        const storeDocRef = doc(db, "stores", cleanStoreId);
        const storePayload = {
          storeId: cleanStoreId,
          storeCode: storeCode.trim().toUpperCase(),
          storeName: storeName.trim(),
          ownerName: ownerName.trim(),
          username: username.trim() || storeCode.trim().toUpperCase(),
          email: email.trim(),
          password: password.trim(), 
          phone: phone.trim(),
          whatsapp: whatsapp.trim(), 
          landline: landline.trim(), 
          role: role,
          status: status,
          address: address.trim(),
          city: city.trim(),
          state: state,
          gstin: gstin.trim().toUpperCase(),
          linkedAuthUid: authUserUid, 
          createdAt: new Date().toISOString() 
        };
        await setDoc(storeDocRef, storePayload);
        setSelectedStoreId(cleanStoreId);
        alert("Success: Cloud architecture documents for store and user profile successfully initialized.");
      }
      resetForm();
    } catch (err) {
      console.error("Store allocation transaction crash: ", err);
      setSystemError(err.message || "Ecosystem data sync mismatch.");
    } finally {
      setIsProcessing(false);
    }
  };

  // --- NEW: FIREBASE PASSWORD RESET HANDLER ---
  const handleSendResetEmail = async () => {
    if (!email) {
      setSystemError("Email address is required to send a reset link.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email.trim());
      alert(`Success: An official Firebase password reset link has been dispatched to ${email}`);
    } catch (err) {
      setSystemError(`Reset Link Failed: ${err.message}`);
    }
  };

  // --- ACTIONS: EDIT & DELETE ---
  const handleEditClick = (store) => {
    setIsEditing(true);
    setEditingStoreId(store.id);
    setLinkedAuthUid(store.linkedAuthUid || null);
    
    setStoreId(store.storeId || store.id);
    setStoreCode(store.storeCode || "");
    setStoreName(store.storeName || "");
    setOwnerName(store.ownerName || "");
    setUsername(store.username || "");
    setEmail(store.email || "");
    setPassword(store.password || ""); 
    setPhone(store.phone || "");
    setWhatsapp(store.whatsapp || ""); 
    setLandline(store.landline || ""); 
    setRole(store.role || "STORE_ADMIN");
    setStatus(store.status || "ACTIVE");
    setAddress(store.address || "");
    setCity(store.city || "");
    setState(store.state || "Bihar");
    setStoreGSTIN(store.gstin || "");
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteStore = async (storeId, storeName) => {
    if (!window.confirm(`SECURITY WARNING: Are you sure you want to permanently delete the store "${storeName}" (${storeId})? This will sever all database connections.`)) return;
    
    try {
      await deleteDoc(doc(db, "stores", storeId));
      if (selectedStoreId === storeId) setSelectedStoreId("");
      if (editingStoreId === storeId) resetForm();
      alert("Store successfully purged from ecosystem.");
    } catch (err) {
      console.error("Purge error: ", err);
      alert("Failed to delete store.");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-900 font-sans antialiased selection:bg-blue-200">
      <Sidebar/>

      <div className="flex-1 p-4 lg:p-8 space-y-6 overflow-x-hidden">
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between p-6 bg-white border border-slate-200 rounded-3xl gap-6 shadow-sm">
          <div>
            <h1 className="text-2xl font-black bg-gradient-to-r from-slate-900 via-slate-700 to-slate-500 bg-clip-text text-transparent uppercase tracking-wider">
              Network Access Platform
            </h1>
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-500">
              <MapPin size={12} className="text-blue-500" />
              <span>Active Target Store Workspace Focus: </span>
              <select value={selectedStoreId} onChange={e => setSelectedStoreId(e.target.value)} className="bg-transparent text-slate-900 font-black outline-none cursor-pointer underline decoration-blue-500 decoration-2">
                {storesList.length === 0 && <option value="">No Active Stores Registered</option>}
                {storesList.map(st => <option key={st.id} value={st.id} className="bg-white text-slate-900">{st.storeName || st.id} ({st.id})</option>)}
              </select>
            </div>
          </div>
        </div>

        <AnimatePresence>
          {systemError && (
            <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-rose-600 text-xs font-bold flex items-center gap-2 shadow-sm">
              <AlertCircle size={14} /> {systemError}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-start">
          
          <div className="xl:col-span-7 space-y-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-4 shadow-sm relative overflow-hidden">
              
              <div className={`absolute top-0 left-0 w-full h-1 ${isEditing ? 'bg-amber-500' : 'bg-blue-600'}`}></div>
              <div className="flex justify-between items-center border-b border-slate-100 pb-2">
                <h2 className={`text-sm font-black uppercase tracking-wider flex items-center gap-2 ${isEditing ? 'text-amber-600' : 'text-blue-600'}`}>
                  {isEditing ? <Edit size={16} /> : <Store size={16} />}
                  {isEditing ? "Update Store Profile Configuration" : "Configure Store Identity Parameters"}
                </h2>
                {isEditing && (
                  <button onClick={resetForm} className="text-[10px] font-bold text-slate-500 hover:text-slate-800 uppercase tracking-wider flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-lg transition-colors">
                    <X size={12}/> Cancel Edit
                  </button>
                )}
              </div>
              
              <form onSubmit={handleSaveStoreProfile} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs pt-2">
                
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold flex justify-between">
                    Store System ID (Unique Key) *
                    {isEditing && <span className="text-[9px] text-amber-500 font-black tracking-widest">(LOCKED)</span>}
                  </label>
                  <input type="text" placeholder="Ex: STORE3637" value={storeId} onChange={e => setStoreId(e.target.value)} disabled={isEditing} className={`w-full p-3 rounded-xl border font-mono font-bold uppercase tracking-wider transition-all ${isEditing ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-50 border-slate-300 text-slate-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none'}`} required />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">Store Functional Code *</label>
                  <input type="text" placeholder="Ex: SPJ001" value={storeCode} onChange={e => setStoreCode(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-blue-700 font-mono uppercase focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" required />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">Branding Store Name *</label>
                  <input type="text" placeholder="Ex: Samastipur Center" value={storeName} onChange={e => setStoreName(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" required />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">Master Owner/Executive Name</label>
                  <input type="text" placeholder="Ex: Sunidhi Sinha" value={ownerName} onChange={e => setOwnerName(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" />
                </div>

                <div className="space-y-1">
                  <label className={`font-bold flex justify-between ${isEditing ? 'text-slate-400' : 'text-amber-600'}`}>
                    Executive Login Email ID *
                    {isEditing && <span className="text-[9px] text-amber-500 font-black tracking-widest">(LOCKED)</span>}
                  </label>
                  <input type="email" placeholder="sunidhi@gmail.com" value={email} onChange={e => setEmail(e.target.value)} disabled={isEditing} className={`w-full p-3 rounded-xl border transition-all ${isEditing ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed' : 'bg-slate-50 border-slate-300 text-slate-900 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none'}`} required />
                </div>
                
                <div className="space-y-1">
                  <label className="font-bold flex justify-between text-amber-600">
                    Ecosystem Security Password {isEditing ? <span className="text-[9px] text-slate-500 font-black">(FIRESTORE SYNC ONLY)</span> : '*'}
                  </label>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className="w-full p-3 rounded-xl border font-mono tracking-widest transition-all bg-slate-50 border-slate-300 text-slate-900 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none" required={!isEditing} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3.5 text-slate-400 hover:text-slate-700 transition-colors bg-transparent border-none p-0">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {/* DEDICATED PASSWORD RESET BUTTON FOR ADMINS */}
                  {isEditing && (
                    <button type="button" onClick={handleSendResetEmail} className="mt-2 w-full py-2 bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-[10px] font-bold uppercase tracking-wider flex justify-center items-center gap-2 hover:bg-slate-200 hover:text-slate-900 transition-colors">
                      <Key size={12} className="text-amber-500" /> Dispatch Auth Reset Email
                    </button>
                  )}
                </div>
                
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">Account Assigned Username</label>
                  <input type="text" placeholder="Ex: SPJ001" value={username} onChange={e => setUsername(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-700 font-mono focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">Mobile Trace Phone String</label>
                  <div className="relative">
                    <Smartphone size={14} className="absolute left-3 top-3.5 text-slate-400" />
                    <input type="tel" maxLength="10" placeholder="6006053427" value={phone} onChange={e => setPhone(e.target.value)} className="w-full pl-9 pr-3 py-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-mono focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-slate-600 font-bold flex justify-between">
                    WhatsApp Number
                  </label>
                  <div className="relative">
                    <Smartphone size={14} className="absolute left-3 top-3.5 text-emerald-500" />
                    <input type="tel" maxLength="10" placeholder="WhatsApp No." value={whatsapp} onChange={e => setWhatsapp(e.target.value)} className="w-full pl-9 pr-3 py-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-mono focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all" />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold flex justify-between">
                    Landline Number <span className="text-[9px] text-slate-400 font-black tracking-widest">(11 DIGITS)</span>
                  </label>
                  <div className="relative">
                    <Phone size={14} className="absolute left-3 top-3.5 text-slate-400" />
                    <input type="tel" maxLength="11" placeholder="Ex: 06122123456" value={landline} onChange={e => setLandline(e.target.value)} className="w-full pl-9 pr-3 py-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 font-mono focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">Security System Access Role</label>
                  <select value={role} onChange={e => setRole(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all">
                    <option value="STORE_ADMIN">STORE_ADMIN (Executive Core)</option>
                    <option value="STORE_MANAGER">STORE_MANAGER (Branch Op)</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">Ecosystem Deployment Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all">
                    <option value="ACTIVE">ACTIVE STATUS NODE</option>
                    <option value="SUSPENDED">SUSPENDED MODULE</option>
                  </select>
                </div>

                <div className="md:col-span-2 space-y-1 border-t border-slate-100 pt-3">
                  <span className="text-[10px] text-slate-400 uppercase tracking-widest font-black block mb-2">Physical Location Specifications</span>
                </div>
                
                <div className="md:col-span-2 space-y-1">
                  <label className="text-slate-600 font-bold">Physical Residential Address</label>
                  <input type="text" placeholder="Main Road, Block B" value={address} onChange={e => setAddress(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">City Profile Bounds</label>
                  <input type="text" placeholder="Begusarai" value={city} onChange={e => setCity(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-600 font-bold">State Jurisdiction Vector</label>
                  <select value={state} onChange={e => setState(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 text-slate-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all">
                    {indianStates.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2 space-y-1">
                  <label className="text-slate-600 font-bold">Branch Corporate GSTIN Matrix Number</label>
                  <input type="text" placeholder="10A524FGDD" value={gstin} onChange={e => setStoreGSTIN(e.target.value)} className="w-full p-3 rounded-xl bg-slate-50 border border-slate-300 font-mono text-slate-900 outline-none uppercase focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all" />
                </div>

                <div className="md:col-span-2 pt-2">
                  <button type="submit" disabled={isProcessing} className={`w-full py-3.5 text-white font-black uppercase tracking-wider rounded-xl shadow-md hover:shadow-lg flex items-center justify-center gap-1 transition-all active:scale-[0.98] ${isEditing ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-gradient-to-r from-blue-600 to-indigo-600'}`}>
                    {isProcessing ? <RefreshCw size={14} className="animate-spin" /> : isEditing ? <Edit size={14} /> : <Plus size={14} />} 
                    {isEditing ? "Commit Profile Updates" : "Provision Identity Core Store Profile"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="xl:col-span-5 space-y-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-4 shadow-sm">
              <h2 className="text-sm font-black uppercase tracking-wider text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-2">
                <Globe size={16} className="text-indigo-500" /> Live Cluster Topologies
              </h2>
              
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1 text-xs">
                {storesList.length === 0 ? (
                  <div className="text-slate-500 py-4 text-center">Zero dynamic store nodes deployed yet inside cloud databases.</div>
                ) : (
                  storesList.map(st => (
                    <div key={st.id} className={`p-4 border rounded-2xl flex flex-col gap-2 transition-all group ${selectedStoreId === st.id ? "border-blue-400 bg-blue-50/30 shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                      
                      <div className="flex items-start justify-between cursor-pointer" onClick={() => setSelectedStoreId(st.id)}>
                        <div>
                          <span className="block text-slate-900 font-black text-sm tracking-tight group-hover:text-blue-700 transition-colors">{st.storeName || "Unspecified Store Name"}</span>
                          <span className={`text-[9px] font-black uppercase tracking-wider mt-1 inline-block px-2 py-0.5 rounded-full ${st.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                            {st.status || 'UNKNOWN'}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 uppercase font-bold">{st.storeCode || "CODE"}</span>
                      </div>
                      
                      <div className="font-mono text-[10px] text-slate-500 space-y-1 mt-1 border-t border-slate-100 pt-2">
                        <p className="flex items-center gap-1"><FileText size={10} className="text-slate-400" /> Email: <span className="text-slate-700">{st.email || "N/A"}</span></p>
                        <p className="flex items-center gap-1"><Smartphone size={10} className="text-slate-400" /> Phone: <span className="text-slate-700">{st.phone || "N/A"}</span></p>
                        {st.landline && <p className="flex items-center gap-1"><Phone size={10} className="text-slate-400" /> Landline: <span className="text-slate-700">{st.landline}</span></p>}
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={() => handleEditClick(st)} className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg font-bold text-[10px] uppercase tracking-wider flex items-center gap-1 transition-colors">
                          <Edit size={10} /> Edit
                        </button>
                        
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}